import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import {
  EVENT_TYPES, EVENT_STATUSES, GUEST_KINDS, GUEST_RESPONSES,
  eventTypeEntry, eventStatusEntry, guestResponseEntry, pl,
} from "../../lib/partnerConstants";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  CalendarDays, Plus, Search, Trash2, MapPin, Users, Euro, Target, ClipboardCheck,
  UserPlus, Building2, Handshake, AlertTriangle, TrendingUp, Download, Mail, FileText,
} from "lucide-react";

const emptyEvent = {
  title: "", event_type: "portes_ouvertes", description: "", level: "residence",
  organizer_maison_id: "", organizer_maison: "", concerned_maison_ids: [],
  start_date: "", start_time: "", end_date: "", end_time: "", location: "",
  capacity: "", target_audience: "", responsible: "", partner_ids: [], guests: [],
  budget: "", actual_cost: "", status: "brouillon", objectives: "", notes: "",
  new_partners_count: 0, new_prospects_count: 0, visits_generated: 0,
  requests_generated: 0, admissions_generated: 0, qualitative_review: "", followup_actions: "",
};

const num = (v) => (v === "" || v == null ? null : Number(v));

export default function EventsTab({ readOnly }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [events, setEvents] = useState([]);
  const [partners, setPartners] = useState([]);
  const [maisons, setMaisons] = useState([]);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fType, setFType] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEvent);
  const [editId, setEditId] = useState(null);
  const [newGuest, setNewGuest] = useState({ name: "", kind: "partner", partner_id: "", email: "", phone: "" });

  const params = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (fStatus) p.status = fStatus;
    if (fType) p.event_type = fType;
    return p;
  }, [q, fStatus, fType]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/crm/events", { params });
      setEvents(r.data.events || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [pa, mm] = await Promise.all([api.get("/crm/partners"), api.get("/my/maisons")]);
        setPartners((pa.data.partners || pa.data || []).filter((x) => !x.archived));
        setMaisons(mm.data.maisons || []);
      } catch { /* non bloquant */ }
    })();
  }, []);

  const openNew = () => { setForm({ ...emptyEvent, concerned_maison_ids: [], partner_ids: [], guests: [] }); setEditId(null); setOpen(true); };
  const openEdit = (ev) => {
    setForm({
      ...emptyEvent, ...ev,
      capacity: ev.capacity ?? "", budget: ev.budget ?? "", actual_cost: ev.actual_cost ?? "",
      concerned_maison_ids: ev.concerned_maison_ids || [], partner_ids: ev.partner_ids || [], guests: ev.guests || [],
    });
    setEditId(ev.id); setOpen(true);
  };

  const save = async (overrideStatus) => {
    if (!form.title.trim()) return toast.error(t("crm.events.titleRequired"));
    const status = overrideStatus || form.status;
    if (status === "realise" && !(form.qualitative_review || "").trim()) {
      return toast.error(t("crm.events.reportRequired"));
    }
    const orgName = maisons.find((m) => m.id === form.organizer_maison_id)?.name || "";
    const payload = {
      ...form, status,
      capacity: num(form.capacity), budget: num(form.budget), actual_cost: num(form.actual_cost),
      organizer_maison: form.level === "group" ? "" : orgName,
      organizer_maison_id: form.level === "group" ? "" : form.organizer_maison_id,
      new_partners_count: Number(form.new_partners_count) || 0,
      new_prospects_count: Number(form.new_prospects_count) || 0,
      visits_generated: Number(form.visits_generated) || 0,
      requests_generated: Number(form.requests_generated) || 0,
      admissions_generated: Number(form.admissions_generated) || 0,
    };
    try {
      if (editId) { await api.put(`/crm/events/${editId}`, payload); toast.success(t("crm.events.saved")); }
      else { const r = await api.post("/crm/events", payload); setEditId(r.data.id); setForm((f) => ({ ...f, ...r.data })); toast.success(t("crm.events.saved")); }
      await load();
      if (overrideStatus) setForm((f) => ({ ...f, status }));
      else setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (ev) => {
    if (!window.confirm(t("crm.events.confirmDelete"))) return;
    try { await api.delete(`/crm/events/${ev.id}`); toast.success(t("crm.events.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { params, responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data); link.download = filename; link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportFiche = async () => {
    if (!editId) return;
    try {
      const res = await api.get(`/crm/export/event/${editId}`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data); link.download = "fiche-evenement.pdf"; link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendInvites = async () => {
    if (!editId) return toast.error(t("crm.events.saveFirst"));
    try {
      const r = await api.post(`/crm/events/${editId}/invite`);
      if (r.data.no_recipient) toast.info(t("crm.events.inviteNoRecipient"));
      else toast.success(t("crm.events.inviteSent", { count: r.data.sent }));
      const rr = await api.get(`/crm/events/${editId}`);
      setForm((f) => ({ ...emptyEvent, ...rr.data, capacity: rr.data.capacity ?? "", budget: rr.data.budget ?? "", actual_cost: rr.data.actual_cost ?? "", concerned_maison_ids: rr.data.concerned_maison_ids || [], partner_ids: rr.data.partner_ids || [], guests: rr.data.guests || [] }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Guests ----
  const guests = form.guests || [];
  const setGuest = (idx, patch) => setForm({ ...form, guests: guests.map((g, i) => (i === idx ? { ...g, ...patch } : g)) });
  const removeGuest = (idx) => setForm({ ...form, guests: guests.filter((_, i) => i !== idx) });
  const addGuest = () => {
    if (!newGuest.name.trim() && !newGuest.partner_id) return toast.error(t("crm.events.guestNameRequired"));
    const name = newGuest.kind === "partner" && newGuest.partner_id
      ? (partners.find((p) => p.id === newGuest.partner_id)?.name || newGuest.name)
      : newGuest.name.trim();
    setForm({ ...form, guests: [...guests, { ...newGuest, name, response: "pending", present: false, invited: false }] });
    setNewGuest({ name: "", kind: "partner", partner_id: "", email: "", phone: "" });
  };

  const convertContact = async (g, idx, target) => {
    if (!editId) return toast.error(t("crm.events.saveFirst"));
    try {
      await api.post(`/crm/events/${editId}/convert-contact`, {
        guest_id: g.id || "", target, name: g.name, phone: g.phone || "", email: g.email || "",
        contact_name: g.name, category: "medical",
      });
      toast.success(target === "partner" ? t("crm.events.convertedPartner") : t("crm.events.convertedProspect"));
      const r = await api.get(`/crm/events/${editId}`);
      setForm((f) => ({ ...emptyEvent, ...r.data, capacity: r.data.capacity ?? "", budget: r.data.budget ?? "", actual_cost: r.data.actual_cost ?? "", concerned_maison_ids: r.data.concerned_maison_ids || [], partner_ids: r.data.partner_ids || [], guests: r.data.guests || [] }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleConcerned = (mid) => {
    const cur = form.concerned_maison_ids || [];
    setForm({ ...form, concerned_maison_ids: cur.includes(mid) ? cur.filter((x) => x !== mid) : [...cur, mid] });
  };
  const togglePartnerInvite = (pid) => {
    const cur = form.partner_ids || [];
    setForm({ ...form, partner_ids: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] });
  };

  const metrics = useMemo(() => {
    const inv = guests.length;
    const conf = guests.filter((g) => g.response === "accepted").length;
    const part = guests.filter((g) => g.present).length;
    const base = conf || inv;
    return { inv, conf, part, rate: base ? Math.round((part / base) * 100) : 0 };
  }, [guests]);

  const postEvent = editId && form.status === "realise";

  return (
    <div className="space-y-4" data-testid="crm-events-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder={t("crm.events.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-event-search" />
        </div>
        <Select value={fStatus || "all"} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-auto min-w-[150px]" data-testid="crm-event-filter-status"><SelectValue placeholder={t("crm.events.allStatuses")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("crm.events.allStatuses")}</SelectItem>{EVENT_STATUSES.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fType || "all"} onValueChange={(v) => setFType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-auto min-w-[150px]" data-testid="crm-event-filter-type"><SelectValue placeholder={t("crm.events.allTypes")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("crm.events.allTypes")}</SelectItem>{EVENT_TYPES.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent>
        </Select>
        {!readOnly && <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-500" size="sm" data-testid="crm-add-event"><Plus className="h-4 w-4 mr-1" /> {t("crm.events.addBtn")}</Button>}
        <Button variant="outline" size="sm" onClick={() => downloadFile("/crm/export/events.csv", "evenements.csv")} data-testid="crm-event-export-csv"><Download className="h-4 w-4 mr-1" /> Excel/CSV</Button>
        <Button variant="outline" size="sm" onClick={() => downloadFile("/crm/export/events.pdf", "evenements.pdf")} data-testid="crm-event-export-pdf"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {events.map((ev) => {
          const st = eventStatusEntry(ev.status);
          const ty = eventTypeEntry(ev.event_type);
          const m = ev.metrics || {};
          return (
            <div key={ev.id} className="rounded-2xl border border-white/10 p-4 bg-white/[0.02] hover:border-emerald-400/30 transition cursor-pointer" data-testid={`crm-event-${ev.id}`} onClick={() => openEdit(ev)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-emerald-400 shrink-0" /> {ev.title}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{ty ? pl(lang, ty) : ev.event_type}</div>
                </div>
                {st && <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: st.color, background: st.color + "22" }}>{pl(lang, st)}</span>}
              </div>
              <div className="mt-2 space-y-1 text-xs text-foreground/70">
                {ev.start_date && <div className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {ev.start_date}{ev.start_time ? ` · ${ev.start_time}` : ""}</div>}
                {ev.location && <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.location}</div>}
                <div className="flex items-center gap-1">
                  {ev.level === "group"
                    ? <><Building2 className="h-3 w-3 text-violet-400" /> <span className="text-violet-300">{t("crm.events.groupLevel")}</span></>
                    : <><Building2 className="h-3 w-3" /> {ev.organizer_maison || "—"}</>}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                <span className="inline-flex items-center gap-1 text-sky-300"><Users className="h-3 w-3" /> {m.invited ?? 0} · {m.confirmed ?? 0} ✓ · {m.participants ?? 0} {t("crm.events.present")}</span>
                {ev.status === "realise" && <span className="inline-flex items-center gap-1 text-emerald-300"><TrendingUp className="h-3 w-3" /> {m.attendance_rate ?? 0}%</span>}
              </div>
            </div>
          );
        })}
        {!events.length && <p className="text-muted-foreground text-sm col-span-full py-8 text-center">{t("crm.events.empty")}</p>}
      </div>

      {/* Dialog création / édition */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-emerald-400" /> {editId ? t("crm.events.edit") : t("crm.events.new")}</DialogTitle>
            <DialogDescription className="sr-only">{t("crm.events.new")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Général */}
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-emerald-300 mb-2">{t("crm.events.sectionGeneral")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2"><Label>{t("crm.events.title")} <span className="text-rose-400">*</span></Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.type")}</Label>
                  <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}><SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{EVENT_TYPES.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label>{t("crm.events.status")}</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="event-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{EVENT_STATUSES.map((s) => <SelectItem key={s.k} value={s.k}>{pl(lang, s)}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5 col-span-2"><Label>{t("crm.events.description")}</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
            </div>

            {/* Organisation */}
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-sky-300 mb-2">{t("crm.events.sectionOrganization")}</p>
              <div className="flex gap-2 mb-3">
                {["residence", "group"].map((lv) => (
                  <button key={lv} type="button" onClick={() => setForm({ ...form, level: lv })} data-testid={`event-level-${lv}`}
                    className="rounded-xl px-3 py-1.5 text-sm font-medium border transition"
                    style={form.level === lv ? { borderColor: "#34d399", background: "rgba(16,185,129,0.14)", color: "#34d399" } : { borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.6)" }}>
                    {lv === "group" ? t("crm.events.groupLevel") : t("crm.events.residenceLevel")}
                  </button>
                ))}
              </div>
              {form.level === "residence" ? (
                <div className="space-y-1.5"><Label>{t("crm.events.organizerResidence")}</Label>
                  <Select value={form.organizer_maison_id || "none"} onValueChange={(v) => setForm({ ...form, organizer_maison_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="event-organizer"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{maisons.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>
              ) : (
                <div className="space-y-1.5"><Label>{t("crm.events.concernedResidences")}</Label>
                  <div className="flex flex-wrap gap-2" data-testid="event-concerned">
                    {maisons.map((m) => (
                      <button key={m.id} type="button" onClick={() => toggleConcerned(m.id)}
                        className="rounded-full px-3 py-1 text-xs border transition"
                        style={(form.concerned_maison_ids || []).includes(m.id) ? { borderColor: "#8b5cf6", background: "rgba(139,92,246,0.16)", color: "#c4b5fd" } : { borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}>
                        {m.name}
                      </button>
                    ))}
                    {!maisons.length && <span className="text-xs text-muted-foreground">{t("crm.events.noResidence")}</span>}
                  </div></div>
              )}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1.5"><Label>{t("crm.events.startDate")}</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="event-start-date" /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.startTime")}</Label><Input type="time" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.endDate")}</Label><Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.endTime")}</Label><Input type="time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.location")}</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.capacity")}</Label><Input type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.targetAudience")}</Label><Input value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.responsible")}</Label><Input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.budget")}</Label><Input type="number" min="0" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("crm.events.actualCost")}</Label><Input type="number" min="0" value={form.actual_cost} onChange={(e) => setForm({ ...form, actual_cost: e.target.value })} data-testid="event-cost" /></div>
                <div className="space-y-1.5 col-span-2"><Label>{t("crm.events.objectives")}</Label><Textarea rows={2} value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} /></div>
              </div>
            </div>

            {/* Partenaires invités */}
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-amber-300 mb-2"><Handshake className="h-3.5 w-3.5 inline mr-1" /> {t("crm.events.invitedPartners")}</p>
              <div className="flex flex-wrap gap-2">
                {partners.map((p) => (
                  <button key={p.id} type="button" onClick={() => togglePartnerInvite(p.id)}
                    className="rounded-full px-3 py-1 text-xs border transition"
                    style={(form.partner_ids || []).includes(p.id) ? { borderColor: "#f59e0b", background: "rgba(245,158,11,0.16)", color: "#fcd34d" } : { borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}>
                    {p.name}
                  </button>
                ))}
                {!partners.length && <span className="text-xs text-muted-foreground">{t("crm.events.noPartners")}</span>}
              </div>
            </div>

            {/* Suivi des invités */}
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-violet-300 mb-2"><Users className="h-3.5 w-3.5 inline mr-1" /> {t("crm.events.guestsTracking")}</p>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-3">
                {/* Ajout */}
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end">
                  <div className="sm:col-span-2 space-y-1"><Label className="text-[11px]">{t("crm.events.guestKind")}</Label>
                    <Select value={newGuest.kind} onValueChange={(v) => setNewGuest({ ...newGuest, kind: v, partner_id: "" })}><SelectTrigger data-testid="event-new-guest-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>{GUEST_KINDS.map((k) => <SelectItem key={k.k} value={k.k}>{pl(lang, k)}</SelectItem>)}</SelectContent></Select></div>
                  {newGuest.kind === "partner" ? (
                    <div className="sm:col-span-3 space-y-1"><Label className="text-[11px]">{t("crm.events.guestPartner")}</Label>
                      <Select value={newGuest.partner_id || "none"} onValueChange={(v) => setNewGuest({ ...newGuest, partner_id: v === "none" ? "" : v, name: partners.find((p) => p.id === v)?.name || "" })}>
                        <SelectTrigger data-testid="event-new-guest-partner"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">—</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
                  ) : (
                    <div className="sm:col-span-3 space-y-1"><Label className="text-[11px]">{t("crm.events.guestName")}</Label><Input value={newGuest.name} onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })} data-testid="event-new-guest-name" /></div>
                  )}
                  <Button type="button" onClick={addGuest} size="sm" className="bg-violet-600 hover:bg-violet-500" data-testid="event-add-guest"><Plus className="h-4 w-4" /></Button>
                </div>
                {/* Liste */}
                {guests.length > 0 && (
                  <div className="space-y-2" data-testid="event-guests-list">
                    {guests.map((g, idx) => {
                      const gr = guestResponseEntry(g.response);
                      const linked = g.prospect_id || g.partner_id;
                      return (
                        <div key={g.id || idx} className="rounded-lg border border-white/10 p-2.5 bg-white/[0.02]">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="text-sm font-medium truncate flex items-center gap-1.5">{g.name || "—"}
                              <span className="text-[10px] text-muted-foreground">({pl(lang, GUEST_KINDS.find((k) => k.k === g.kind))})</span></span>
                            <div className="flex items-center gap-2">
                              <Select value={g.response} onValueChange={(v) => setGuest(idx, { response: v })}>
                                <SelectTrigger className="h-7 w-auto min-w-[110px] text-xs" data-testid={`event-guest-response-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>{GUEST_RESPONSES.map((r) => <SelectItem key={r.k} value={r.k}>{pl(lang, r)}</SelectItem>)}</SelectContent></Select>
                              <label className="flex items-center gap-1 text-xs cursor-pointer"><Checkbox checked={!!g.present} onCheckedChange={(v) => setGuest(idx, { present: !!v })} data-testid={`event-guest-present-${idx}`} /> {t("crm.events.present")}</label>
                              <button type="button" onClick={() => removeGuest(idx)} className="text-rose-400 hover:opacity-80"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          </div>
                          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                            <Input value={g.comment || ""} onChange={(e) => setGuest(idx, { comment: e.target.value })} placeholder={t("crm.events.guestComment")} className="h-7 text-xs flex-1 min-w-[140px]" />
                            {gr && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: gr.color, background: gr.color + "22" }}>{pl(lang, gr)}</span>}
                            {g.kind === "contact" && !linked && editId && (
                              <span className="flex gap-1">
                                <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => convertContact(g, idx, "prospect")} data-testid={`event-convert-prospect-${idx}`}><UserPlus className="h-3 w-3 mr-1" /> {t("crm.events.toProspect")}</Button>
                                <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => convertContact(g, idx, "partner")} data-testid={`event-convert-partner-${idx}`}><Handshake className="h-3 w-3 mr-1" /> {t("crm.events.toPartner")}</Button>
                              </span>
                            )}
                            {linked && <span className="text-[10px] text-emerald-300">✓ {t("crm.events.linked")}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Récap */}
                <div className="flex flex-wrap gap-3 text-xs pt-1 border-t border-white/10">
                  <span className="text-sky-300">{t("crm.events.invited")}: <b>{metrics.inv}</b></span>
                  <span className="text-emerald-300">{t("crm.events.confirmed")}: <b>{metrics.conf}</b></span>
                  <span className="text-amber-300">{t("crm.events.participants")}: <b>{metrics.part}</b></span>
                  <span className="text-violet-300">{t("crm.events.attendanceRate")}: <b>{metrics.rate}%</b></span>
                </div>
              </div>
            </div>

            {/* Résultats / bilan */}
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-rose-300 mb-2 flex items-center gap-1"><ClipboardCheck className="h-3.5 w-3.5" /> {t("crm.events.sectionResults")}
                {postEvent && <span className="text-[10px] text-rose-400 normal-case font-semibold">· {t("crm.events.reportMandatory")}</span>}</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  ["new_partners_count", t("crm.events.newPartners")],
                  ["new_prospects_count", t("crm.events.newProspects")],
                  ["visits_generated", t("crm.events.visitsGen")],
                  ["requests_generated", t("crm.events.requestsGen")],
                  ["admissions_generated", t("crm.events.admissionsGen")],
                ].map(([k, lbl]) => (
                  <div key={k} className="space-y-1"><Label className="text-[11px]">{lbl}</Label>
                    <Input type="number" min="0" value={form[k] ?? 0} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`event-${k}`} /></div>
                ))}
              </div>
              {(Number(form.actual_cost) > 0) && (
                <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Euro className="h-3 w-3" /> {t("crm.events.costPerParticipant")}: <b className="text-foreground/80">{metrics.part ? (Number(form.actual_cost) / metrics.part).toFixed(2) : "—"}</b></span>
                  <span className="inline-flex items-center gap-1"><Euro className="h-3 w-3" /> {t("crm.events.costPerProspect")}: <b className="text-foreground/80">{Number(form.new_prospects_count) ? (Number(form.actual_cost) / Number(form.new_prospects_count)).toFixed(2) : "—"}</b></span>
                  <span className="inline-flex items-center gap-1"><Euro className="h-3 w-3" /> {t("crm.events.costPerAdmission")}: <b className="text-foreground/80">{Number(form.admissions_generated) ? (Number(form.actual_cost) / Number(form.admissions_generated)).toFixed(2) : "—"}</b></span>
                </div>
              )}
              <div className="space-y-1.5 mt-3">
                <Label className={postEvent ? "text-rose-300" : ""}>{t("crm.events.qualitativeReview")} {postEvent && <span className="text-rose-400">*</span>}</Label>
                <Textarea rows={2} value={form.qualitative_review} onChange={(e) => setForm({ ...form, qualitative_review: e.target.value })} placeholder={t("crm.events.reviewPlaceholder")} data-testid="event-review" />
              </div>
              <div className="space-y-1.5 mt-2"><Label>{t("crm.events.followupActions")}</Label><Textarea rows={2} value={form.followup_actions} onChange={(e) => setForm({ ...form, followup_actions: e.target.value })} placeholder={t("crm.events.followupPlaceholder")} /></div>
              <div className="space-y-1.5 mt-2"><Label>{t("crm.events.internalNotes")}</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>

            {/* Suggestions post-événement */}
            {postEvent && (
              <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 text-xs text-amber-200/90" data-testid="event-post-suggestions">
                <div className="font-semibold flex items-center gap-1 mb-1"><AlertTriangle className="h-3.5 w-3.5" /> {t("crm.events.postSuggestTitle")}</div>
                <ul className="space-y-0.5 list-disc list-inside">
                  <li>{t("crm.events.postSuggest1")}</li>
                  <li>{t("crm.events.postSuggest2")}</li>
                  <li>{t("crm.events.postSuggest3")}</li>
                  <li>{t("crm.events.postSuggest4")}</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            {editId && !readOnly && <Button variant="outline" onClick={() => { del({ id: editId }); setOpen(false); }} className="text-rose-400 mr-auto"><Trash2 className="h-4 w-4 mr-1" /> {t("crm.actions.delete")}</Button>}
            {editId && <Button variant="outline" onClick={exportFiche} data-testid="event-export-fiche"><FileText className="h-4 w-4 mr-1" /> {t("crm.events.exportFiche")}</Button>}
            {editId && !readOnly && <Button variant="outline" onClick={sendInvites} data-testid="event-invite"><Mail className="h-4 w-4 mr-1" /> {t("crm.events.sendInvites")}</Button>}
            <Button variant="outline" onClick={() => setOpen(false)}>{t("crm.actions.cancel")}</Button>
            {!readOnly && <Button onClick={() => save()} className="bg-emerald-600 hover:bg-emerald-500" data-testid="event-save">{t("crm.actions.save")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
